import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, TrendingUp, ExternalLink, Clock } from 'lucide-react';
import SEO from '../components/SEO';

const NewsPage = () => {
  const newsItems = [
    {
      id: 1,
      slug: 'digital-trademark-registrations-surge-45-percent',
      title: 'Digital Trademark Registrations Surge 45% in 2024',
      excerpt: 'Record-breaking year as businesses rush to protect their digital brands. USPTO reports unprecedented demand driven by e-commerce expansion and online business growth.',
      date: 'December 8, 2024',
      category: 'Industry Trends',
      featured: true,
      source: 'Trademark Report',
      image: 'https://images.unsplash.com/photo-1726056652641-de93ec003289?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      slug: 'major-ecommerce-counterfeiting-ring-dismantled',
      title: 'Major E-commerce Counterfeiting Ring Dismantled',
      excerpt: 'Federal authorities shut down international operation responsible for over $50 million in counterfeit trademark goods sold on major e-commerce platforms.',
      date: 'December 6, 2024',
      category: 'Legal Action',
      featured: true,
      source: 'Legal News Today',
      image: 'https://images.unsplash.com/photo-1763569378571-50ef8417b15f?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      slug: 'new-uspto-ai-tool-speeds-trademark-search',
      title: 'New USPTO AI Tool Speeds Up Trademark Search Process',
      excerpt: 'Revolutionary artificial intelligence system reduces trademark search time by 60%, helping businesses identify potential conflicts faster than ever before.',
      date: 'December 4, 2024',
      category: 'Technology',
      featured: false,
      source: 'USPTO Updates',
      image: 'https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      slug: 'global-brand-protection-summit-2025',
      title: 'Global Brand Protection Summit Announces 2025 Dates',
      excerpt: 'Premier international event for brand protection professionals will bring together industry leaders, legal experts, and enforcement agencies next April.',
      date: 'December 2, 2024',
      category: 'Events',
      featured: false,
      source: 'Industry News',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      slug: 'small-business-trademark-fees-reduced',
      title: 'Small Business Trademark Filing Fees Reduced by 30%',
      excerpt: 'USPTO announces new fee structure to support startups and small businesses, making federal trademark protection more accessible and affordable.',
      date: 'November 30, 2024',
      category: 'USPTO Updates',
      featured: false,
      source: 'USPTO Official',
      image: 'https://images.unsplash.com/photo-1648747067003-0e4660db791f?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      slug: 'cryptocurrency-trademark-applications-double',
      title: 'Cryptocurrency Trademark Applications Double This Quarter',
      excerpt: 'Explosive growth in crypto and blockchain sector drives record trademark filings as companies race to establish their digital asset brands.',
      date: 'November 28, 2024',
      category: 'Industry Trends',
      featured: false,
      source: 'Crypto Legal Report',
      image: 'https://images.unsplash.com/photo-1590285836796-f772deafabfc?w=800&h=600&fit=crop'
    },
    {
      id: 7,
      slug: 'fashion-brands-unite-against-infringement',
      title: 'Fashion Brands Unite Against Trademark Infringement',
      excerpt: 'Leading fashion houses form unprecedented coalition to combat counterfeit products and protect brand integrity across global markets.',
      date: 'November 25, 2024',
      category: 'Industry News',
      featured: false,
      source: 'Fashion Business',
      image: 'https://images.pexels.com/photos/35074518/pexels-photo-35074518.jpeg?w=800&h=600&fit=crop'
    },
    {
      id: 8,
      slug: 'mobile-app-simplifies-trademark-monitoring',
      title: 'New Mobile App Simplifies Trademark Monitoring',
      excerpt: 'Innovative smartphone application allows businesses to track and manage their trademark portfolio on-the-go with real-time infringement alerts.',
      date: 'November 22, 2024',
      category: 'Technology',
      featured: false,
      source: 'Tech Innovation',
      image: 'https://images.unsplash.com/photo-1730817403158-b30479c8d473?w=800&h=600&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Trademark & IP Industry News | Norfolk Trademark"
        description="Latest trademark and IP news, USPTO updates, and industry trends from Norfolk Trademark experts."
        keywords="trademark news, IP industry news, USPTO updates, trademark trends"
        canonical="/resources/news"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-[#1a1a1a] to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              Latest Updates
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trademark News & Updates
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Stay ahead with the latest trademark law updates, USPTO announcements, and industry developments
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured News */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Stories</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {newsItems.filter(item => item.featured).map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#FF035B] text-white px-3 py-2 rounded-lg text-sm font-semibold">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{news.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ExternalLink className="w-4 h-4" />
                      <span>{news.source}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#FF035B] transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {news.excerpt}
                  </p>
                  <Link
                    to={`/resources/news/${news.slug}`}
                    className="inline-flex items-center gap-2 text-[#FF035B] font-semibold hover:gap-3 transition-all"
                  >
                    Read Full Story
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* All News */}
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="h-1 w-12 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">Recent News</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {newsItems.filter(item => !item.featured).map((news) => (
              <article
                key={news.id}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold">
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <Calendar className="w-3 h-3" />
                    <span>{news.date}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#FF035B] transition-colors line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {news.excerpt}
                  </p>
                  <Link
                    to={`/resources/news/${news.slug}`}
                    className="inline-flex items-center gap-1 text-[#FF035B] font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Read More
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Load More */}
        <div className="text-center mt-12">
          <button className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105">
            Load More News
          </button>
        </div>
      </div>

      {/* Subscribe Section */}
      <div className="bg-gradient-to-br from-[#FF035B] via-[#2F1A8E] to-[#FFA06B] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Get News Alerts
          </h2>
          <p className="text-white/90 mb-8">
            Subscribe to receive breaking news and important updates about trademark law
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="bg-black hover:bg-gray-900 text-white font-bold px-6 py-3 rounded-lg transition-all duration-300">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
